import { type ReactNode, useRef } from 'react';

import TMSProvider from 'components/TMSProvider';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { getMcleodIntegrationFromList } from 'pages/QuoteView/LoadBuilding/utils/tmsIntegrations';
import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

import { SharepointLoadBuildingSyncHost } from '../hooks/useSharepointLoadBuildingSync';
import { logSharepointLbDiag } from '../lib/sharepointLoadBuildingDiagnostics';
import { SharepointEwaAgileRowsProvider } from '../providers/SharepointEwaAgileRowsProvider';
import { SharepointLoadBuildingProcessingProvider } from '../providers/SharepointLoadBuildingProcessingProvider';
import { SharepointWorkbookGraphProvider } from '../providers/SharepointWorkbookGraphProvider';

/**
 * SharePoint load-building: McLeod uses EWA-scraped Agile rows; Turvo uses Microsoft Graph for
 * backend-produced suggestions and EWA/DOM/URL only for active worksheet selection (see
 * `sharepointLoadBuildingDataSourceFromUrl`).
 *
 * Lives under {@link pages/QuoteView/QuoteSidebar QuoteSidebar} so work starts as soon as the
 * quote sidebar opens. Previously this lived inside {@link LoadBuildingTab}; inactive tab
 * panels often mount late or after TMS flags from `useEffect`, so the hammer badge and rows
 * appeared only after visiting Load Building.
 *
 * Only `SharepointLoadBuildingSyncHost` sits under a dedicated SharePoint `TMSProvider`; sidebar
 * tabs stay off it so `useTMSContext` is unchanged for Quick Quote / Carrier Quote / Truck List.
 */
type ExcelSharepointLoadBuildingHostProps = {
  children?: ReactNode;
};

export default function ExcelSharepointLoadBuildingHost({
  children,
}: ExcelSharepointLoadBuildingHostProps) {
  const {
    isLoading,
    tmsIntegrations,
    serviceFeaturesEnabled: { isLoadBuildingEnabled },
  } = useServiceFeatures();

  const mcleodIntegration = getMcleodIntegrationFromList(tmsIntegrations);
  const turvoIntegration = tmsIntegrations?.find(
    (tms) => tms.name === TMS.Turvo
  );
  const sharepointTMSIntegration = mcleodIntegration ?? turvoIntegration;
  const useGraphForSharepoint = sharepointTMSIntegration?.name === TMS.Turvo;
  const useEwaAgileRowsProvider = mcleodIntegration != null;

  const lastMountBlockReasonRef = useRef<Maybe<string>>(null);

  if (isLoading || !isLoadBuildingEnabled || !sharepointTMSIntegration) {
    if (!isLoading) {
      const blockReason = !isLoadBuildingEnabled
        ? 'isLoadBuildingEnabled=false'
        : 'noSharepointTmsIntegration';
      if (lastMountBlockReasonRef.current !== blockReason) {
        lastMountBlockReasonRef.current = blockReason;
        logSharepointLbDiag('sharepointHostMountBlocked', {
          blockReason,
          turvoIntegrationId: turvoIntegration?.id,
          mcleodIntegrationId: mcleodIntegration?.id,
        });
      }
    }
    return <>{children}</>;
  }

  const tmsSubtree = (
    <>
      <TMSProvider
        tmsID={sharepointTMSIntegration.id}
        tmsName={sharepointTMSIntegration.name}
        tenant={sharepointTMSIntegration.tenant}
        isTestingEnvironment={
          sharepointTMSIntegration.isTestingEnvironment ?? false
        }
      >
        <SharepointLoadBuildingSyncHost enabled={true} />
      </TMSProvider>
      {children}
    </>
  );

  let tree: ReactNode = tmsSubtree;
  if (useGraphForSharepoint) {
    tree = (
      <SharepointWorkbookGraphProvider enabled={true}>
        {tree}
      </SharepointWorkbookGraphProvider>
    );
  }
  if (useEwaAgileRowsProvider) {
    tree = (
      <SharepointEwaAgileRowsProvider enabled={true}>
        {tree}
      </SharepointEwaAgileRowsProvider>
    );
  }
  return (
    <SharepointLoadBuildingProcessingProvider>
      {tree}
    </SharepointLoadBuildingProcessingProvider>
  );
}
